import { Component, Input, OnInit } from '@angular/core';
import { ApiClient } from '@app/clients';
import { BaseComponent } from '../../base.component';
import { ApiDomain, ApiResources, ApiVersion } from '../../enums';
import { ApiResponse, GuestData, Member, MemberData, Venue } from '../../models';

export interface MemberLookupRow {
  member: Member;
  guest: GuestData | null;
}

@Component({
  selector: 'app-member-lookup',
  templateUrl: './member-lookup.component.html',
  styleUrls: ['./member-lookup.component.scss'],
})
export class MemberLookupComponent extends BaseComponent implements OnInit {
  @Input() venue: Venue;

  public searchOption: 'fullName' | 'memberId' = 'fullName';

  public searchValue: string;

  public rows: MemberLookupRow[] = [];

  public selectedRow: MemberLookupRow | null = null;

  public hasSearched = false;

  constructor() {
    super();
    this.apiClient.setResource(ApiResources.MEMBER);
  }

  public ngOnInit() {
    this.init();
  }

  public get selectedGuestOrders(): number {
    return this.selectedRow?.guest?.ordersSummary?.orders ?? 0;
  }

  public get selectedGuestLifetimeSpend(): number {
    return this.selectedRow?.guest?.ordersSummary?.total ?? 0;
  }

  public getInitials(name: string): string {
    return (
      (name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join('') || 'M'
    );
  }

  public selectRow(row: MemberLookupRow) {
    this.selectedRow = row;
  }

  public trackByMemberId(index: number, row: MemberLookupRow): string {
    return row.member?.id ? String(row.member.id) : `idx-${index}`;
  }

  public async search() {
    await this.loader.show('Searching members...');
    try {
      this.apiClient.setResource(ApiResources.MEMBER);
      const searchResult = await this.makeSafeRequest<ApiResponse.SearchResult<MemberData>>(
        this.apiClient.get({
          [this.searchOption]: this.searchValue,
        })
      );
      this.hasSearched = true;
      this.selectedRow = null;
      if (!searchResult?.success) {
        this.rows = [];
        return;
      }
      const members = searchResult.data.data.map(memberData => new Member(memberData));
      await this.hydrateGuestsForMembers(members);
      if (this.rows.length === 1) {
        this.selectedRow = this.rows[0];
      }
    } finally {
      await this.loader.hide();
    }
  }

  private guestApiClient(): ApiClient {
    return new ApiClient().setDomain(ApiDomain.API, ApiVersion.V2).setResource(ApiResources.GUESTS);
  }

  private async hydrateGuestsForMembers(members: Member[]): Promise<void> {
    const guestClient = this.guestApiClient();
    this.rows = await Promise.all(
      members.map(async member => {
        const memberId = member.id;
        let guest: GuestData | null = null;
        if (memberId) {
          const guestResult = await this.makeSafeRequest(
            guestClient.get<ApiResponse.SearchResult<GuestData>>({ memberId })
          );
          if (guestResult?.success && guestResult.data?.data?.length) {
            guest = guestResult.data.data[0] as GuestData;
          }
        }
        return { member, guest };
      })
    );
  }

  public confirmSelection() {
    if (!this.selectedRow) {
      return;
    }
    this.modalCtrl.dismiss({ member: this.selectedRow.member });
  }

  public dismiss() {
    this.modalCtrl.dismiss();
  }
}
